/**
 * The guide the beer scan matches a menu against: 56 beer styles in 7 families,
 * each rated 0–5 on 8 flavour axes (from the testkitchen beer-guide prototype;
 * ABV, IBU and colour follow the BJCP 2021 guidelines, the ratings are editorial).
 * Nothing is translated: a line counts when it prints a style's name.
 */
import beerData from '../data/beerStyles.json';
import { createMatcher, GuideEntry } from './guideMatcher';

/** `a`: ratings in BEER_AXES order. `aka`: other names a menu prints the style under, English and katakana. */
export interface BeerStyle { kind: 'style'; key: string; id: string; n: string; f: string; abv: number[]; ibu: number[]; srm: number; o: string; d: string; p: string; g: string; t: string; x: string[]; a: number[]; aka: string[] }
/** A name that doesn't settle on one style: the menu says "IPA", not which IPA. */
export interface BeerWord { kind: 'word'; key: string; id: string; n: string; aka: string[]; styles: string[]; note?: string }
export type BeerItem = BeerStyle | BeerWord;

export interface BeerAxis { k: string; label: string; short: string; words: string[] }

interface RawBeer {
  AXES: BeerAxis[];
  STYLES: Omit<BeerStyle, 'kind' | 'key'>[];
  WORDS: Omit<BeerWord, 'kind' | 'key'>[];
}
const beer = beerData as unknown as RawBeer;

export const BEER_AXES = beer.AXES;
export const axisIndex = (k: string) => BEER_AXES.findIndex((a) => a.k === k);

export const BEER_ITEMS = new Map<string, BeerItem>();
for (const s of beer.STYLES) BEER_ITEMS.set(`s-${s.id}`, { ...s, kind: 'style', key: `s-${s.id}` });
for (const w of beer.WORDS) BEER_ITEMS.set(`w-${w.id}`, { ...w, kind: 'word', key: `w-${w.id}`, styles: w.styles.map((id) => `s-${id}`) });

// A style's own name wins over a word for several: "Bock" is the style, not a family of them
const beerEntries: GuideEntry[] = [];
for (const it of BEER_ITEMS.values()) {
  const rank = it.kind === 'style' ? 0 : 1;
  // "Hazy / New England IPA" is two names in the guide; its `aka` lists them apart
  if (!it.n.includes('/')) beerEntries.push({ raw: it.n, key: it.key, rank });
  it.aka.forEach((a) => beerEntries.push({ raw: a, key: it.key, rank }));
}

export const findBeers = createMatcher({
  entries: beerEntries,
  // A style's name is often the end of a longer one (チョコレートスタウト, クラフトラガー): no kana guards
  guardMax: 0,
  stop: ['ボックス', 'アルトバイエルン', 'リアルト', 'グーズベリー', 'ポーターハウス'],
  // IPA, ESB, APA, Alt, Wit
  minLatin: 3,
});

/** The styles a name stands for: itself, or a word's several. */
export const stylesOf = (it: BeerItem): BeerStyle[] =>
  (it.kind === 'style' ? [it] : it.styles.map((k) => BEER_ITEMS.get(k) as BeerStyle));

/** Each axis's lowest and highest across the styles a name stands for (the same number twice for one style). */
export const beerRanges = (it: BeerItem): [number, number][] => {
  const styles = stylesOf(it);
  return BEER_AXES.map((_, k) => [Math.min(...styles.map((s) => s.a[k])), Math.max(...styles.map((s) => s.a[k]))]);
};

/** A name's ratings: a style's own, or for a word like IPA the average of its styles'. */
export const beerRatings = (it: BeerItem) => {
  const styles = stylesOf(it);
  return BEER_AXES.map((_, k) => styles.reduce((a, s) => a + s.a[k], 0) / styles.length);
};

/** A taste a menu's beer is grouped by, and the guide's axes (indexes into BEER_AXES) that make it up. */
export interface BeerTaste { k: string; label: string; hint: string; axes: number[] }
const taste = (k: string, label: string, hint: string, ...axes: string[]): BeerTaste => ({ k, label, hint, axes: axes.map(axisIndex) });

// Body and strength aren't tastes: a beer with no taste rated 3 or more is crisp, and its body is what there is to say about it
const CRISP = taste('crisp', 'Crisp & clean', 'Light and easy; nothing shouts', 'body');
const BOLD = [
  taste('hoppy', 'Hoppy & bitter', 'Citrus, pine, a bitter finish', 'bitter', 'hop'),
  taste('fruity', 'Fruity & spicy', 'Banana, clove, stone fruit, pepper', 'fruit'),
  taste('malty', 'Malty & sweet', 'Bread, caramel, toffee', 'sweet'),
  taste('roasty', 'Roasty', 'Coffee, cocoa, dark toast', 'roast'),
  taste('sour', 'Sour & tart', 'Sharp, bright, mouth-watering', 'sour'),
];
/** Lightest to most intense */
export const BEER_TASTES = [CRISP, ...BOLD];

const tasteLevel = (t: BeerTaste, a: number[]) => Math.max(...t.axes.map((k) => a[k]));
// How much of each taste the guide's styles have on average
const USUAL = new Map(BOLD.map((t) => [t.k, beer.STYLES.reduce((a, s) => a + tasteLevel(t, s.a), 0) / beer.STYLES.length]));

/** `level`: how much of the taste, 0–5 (for crisp, how light). `also`: a second taste it's strong in. */
export interface Tasted { taste: BeerTaste; level: number; also?: BeerTaste }

/**
 * The taste a name is grouped under: of the tastes it's rated 3 or more for,
 * the one it has most of next to the average beer in the guide. A milk stout
 * is rated sweeter than it is roasty, but most beer is sweet and little is
 * roasty, so it's roasty (and also sweet).
 */
export function beerTaste(it: BeerItem): Tasted {
  const a = beerRatings(it);
  const apart = (t: BeerTaste) => tasteLevel(t, a) - USUAL.get(t.k)!;
  const strong = BOLD.filter((t) => tasteLevel(t, a) >= 3).sort((x, y) => apart(y) - apart(x));
  if (!strong.length) return { taste: CRISP, level: 5 - tasteLevel(CRISP, a) };
  return { taste: strong[0], level: tasteLevel(strong[0], a), also: strong[1] };
}

// The bands of a "style of beer" glass chart, pale lager down to stout, each at the SRM that beer is brewed at
const SRM_STOPS: [number, string][] = [
  [3, '#D9963F'], [5, '#CF6538'], [6.5, '#B5412D'], [8, '#C83037'], [10, '#C42A36'],
  [13, '#BE2633'], [16, '#981C2B'], [19, '#821B27'], [25, '#281D21'], [33, '#1E1719'],
];

const rgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

/** Roughly the colour of the beer in the glass, from its SRM: blended between the bands either side. */
export function srmToHex(srm: number) {
  const k = SRM_STOPS.findIndex(([at]) => srm <= at);
  if (k <= 0) return SRM_STOPS[k < 0 ? SRM_STOPS.length - 1 : 0][1];
  const [lo, from] = SRM_STOPS[k - 1], [hi, to] = SRM_STOPS[k];
  const t = (srm - lo) / (hi - lo), a = rgb(from), b = rgb(to);
  return `#${a.map((v, i) => Math.round(v + (b[i] - v) * t).toString(16).padStart(2, '0')).join('')}`;
}

/** A name's colour: a style's own, or for a word like IPA the colour midway between its styles'. */
export const beerColor = (it: BeerItem) => {
  const styles = stylesOf(it);
  return srmToHex(styles.reduce((a, s) => a + s.srm, 0) / styles.length);
};

/** Dark or light text, whichever reads on a beer's colour. */
export function inkOn(hex: string) {
  const [r, g, b] = rgb(hex);
  return (r * 299 + g * 587 + b * 114) / 1000 > 140 ? '#1f1b16' : '#ffffff';
}

const range = (v: number[], digits = 0) => (v[0] === v[1] ? v[0].toFixed(digits) : `${v[0].toFixed(digits)}–${v[1].toFixed(digits)}`);
export const abvText = (s: BeerStyle) => `${range(s.abv, 1)}%`;
export const ibuText = (s: BeerStyle) => range(s.ibu);
